import classNames from "classnames";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { useToast } from "hooks/useToast";
import { useModal } from "hooks/useModal";
import {
  INCORPORATION_FORM_SOURCE,
  IncorporationFormSource,
} from "constants/incorporation";
import ThreeDots from "static/images/ThreeDots.svg";
import { Stage } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { MouseEvent } from "react";
import { IncorporationAddStageControl } from "./IncorporationAddStageControl";
import { SaveStageAsTemplateModal } from "./SaveStageAsTemplateModal";
import { IncorporationLockedFieldTooltip } from "./IncorporationLockedFieldTooltip";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { Padlock10 } from "components/icons/Padlock10";
import {
  getStageProgress,
  isStageComplete,
} from "util/incorporationFormProgress";
import {
  useCreateFormTreeNodeMutation,
  useDeleteFormTreeNodeMutation,
} from "./useFormTreeNodeMutations";
import { TickSlim } from "components/icons/TickSlim";

const IncorporationStageCardActions = ({
  source,
  treeId,
  stage,
  onAfterDelete,
  isDeleteDisabled = false,
}: {
  source: IncorporationFormSource;
  treeId: string;
  stage: Stage;
  onAfterDelete: () => void;
  isDeleteDisabled?: boolean;
}) => {
  const { alertToast } = useToast();
  const saveAsTemplateModal = useModal();
  const [duplicateStage] = useCreateFormTreeNodeMutation({ source });
  const [deleteStage] = useDeleteFormTreeNodeMutation({ source });

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleDuplicate = async () => {
    try {
      const { uuid, status, is_saved_as_template, ...stageData } = stage;
      await duplicateStage({
        treeId,
        body: { stage: { ...stageData, parent_id: treeId } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteStage({
        treeId,
        body: { stage: { node_id: stage.uuid } },
      }).unwrap();
      onAfterDelete();
    } catch (err) {
      onActionError(err);
    }
  };

  return (
    <div onClick={handleClick}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button customType="ghost_icon" size="xsmall">
            <img src={ThreeDots} alt="Show more options" />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={6} align="end" className="!t-min-w-40">
            <Dropdown.Item onSelect={handleDuplicate}>
              Duplicate stage
            </Dropdown.Item>
            <Dropdown.Item onSelect={saveAsTemplateModal.open}>
              Save as new base stage
            </Dropdown.Item>
            <Dropdown.Item
              type="danger"
              disabled={isDeleteDisabled}
              onSelect={handleDelete}
            >
              <IncorporationLockedFieldTooltip isLocked={isDeleteDisabled}>
                <span>Delete stage</span>
              </IncorporationLockedFieldTooltip>
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <SaveStageAsTemplateModal
        isOpen={saveAsTemplateModal.isOpen}
        close={saveAsTemplateModal.close}
        source={source}
        treeId={treeId}
        stageId={stage.uuid}
      />
    </div>
  );
};

const IncorporationStageCard = ({
  stage,
  order,
  isSelected,
  isEditable,
  isLocked,
  isPublished,
  source,
  treeId,
  onSelect,
  onAfterDelete,
}: {
  stage: Stage;
  order: number;
  isSelected: boolean;
  isEditable: boolean;
  isLocked: boolean;
  isPublished: boolean;
  source: IncorporationFormSource;
  treeId: string;
  onSelect: () => void;
  onAfterDelete: () => void;
}) => {
  const { answeredCount, totalCount } = getStageProgress(stage);

  return (
    <div
      onClick={onSelect}
      className={classNames(
        "t-flex t-cursor-pointer t-flex-col t-gap-0.5 t-rounded-lg t-px-4 t-py-3 sm:t-w-44 sm:t-shrink-0",
        {
          "t-bg-purple-0": isSelected,
          "hover:t-bg-surface-lighter-grey": !isSelected,
        }
      )}
    >
      <div className="t-flex t-items-center t-justify-between">
        <span
          className={classNames("t-text-caption t-text-neutral-30", {
            "t-text-neutral-50": isSelected,
          })}
        >
          Stage {order}
        </span>
        {isEditable && (
          <IncorporationStageCardActions
            source={source}
            treeId={treeId}
            stage={stage}
            onAfterDelete={onAfterDelete}
            isDeleteDisabled={isPublished && isStageComplete(stage)}
          />
        )}
      </div>
      <div className="t-flex t-items-center t-gap-1.5">
        {isLocked && (
          <span className="t-flex t-shrink-0 t-text-text-30">
            <Padlock10 />
          </span>
        )}
        <ToolTip text={stage.title || "Untitled stage"}>
          <span className="t-block t-truncate t-text-subtext t-text-text-60">
            {stage.title || "Untitled stage"}
          </span>
        </ToolTip>
      </div>
      {!isEditable &&
        !isLocked &&
        isPublished &&
        source !== INCORPORATION_FORM_SOURCE.TEMPLATE && (
          <span className="t-text-body-sm-italic t-italic t-text-text-30">
            {totalCount > 0 && answeredCount === totalCount ? (
              <>
                <TickSlim />
                {"  Completed"}
              </>
            ) : (
              `${answeredCount} of ${totalCount} completed`
            )}
          </span>
        )}
    </div>
  );
};

export const IncorporationStageSidebar = ({
  stages,
  selectedStageId,
  onSelectStage,
  source,
  parentId,
  isEditable,
  isAnswerable = false,
  isPublished = false,
}: {
  stages: Stage[];
  selectedStageId: string | null;
  onSelectStage: (stageId: string | null) => void;
  source: IncorporationFormSource;
  parentId: string;
  isEditable: boolean;
  isAnswerable?: boolean;
  isPublished?: boolean;
}) => (
  <div className="t-flex t-h-full t-min-h-0 t-w-64 t-shrink-0 t-flex-col t-border-0 t-border-r t-border-solid t-border-neutral-10 sm:t-h-auto sm:t-w-full sm:t-border-b sm:t-border-r-0">
    <div className="t-flex t-min-h-0 t-flex-1 t-flex-col t-gap-1.5 t-overflow-y-auto t-py-3 t-pr-3 sm:t-flex-row sm:t-overflow-x-auto sm:t-px-0 sm:t-py-2">
      <span
        className={classNames("t-py-1 sm:t-hidden", {
          "t-text-body-sm t-text-text-60": isEditable,
          "t-text-body-sm t-text-neutral-50": !isEditable,
        })}
      >
        Formation Stages
      </span>
      {stages.map((stage, index) => {
        const isActiveStage = stage.uuid === selectedStageId;
        const nextStage = stages[index + 1] ?? stages[index - 1] ?? null;
        const previousStage = stages[index - 1];
        const isLocked = Boolean(
          isAnswerable && previousStage && !isStageComplete(previousStage)
        );

        const onAfterDelete = () => {
          if (isActiveStage) {
            onSelectStage(nextStage?.uuid ?? null);
          }
        };

        return (
          <IncorporationStageCard
            key={stage.uuid}
            stage={stage}
            order={index + 1}
            isSelected={isActiveStage}
            isEditable={isEditable}
            isLocked={isLocked}
            isPublished={isPublished}
            source={source}
            treeId={parentId}
            onSelect={() => onSelectStage(stage.uuid)}
            onAfterDelete={onAfterDelete}
          />
        );
      })}
      {isEditable && (
        <IncorporationAddStageControl
          source={source}
          parentId={parentId}
          onStageCreated={onSelectStage}
        />
      )}
    </div>
  </div>
);
